<template>
    <Paper class="px-3 pt-2 pb-3">
        <div v-if="isLoading">
            <DetailModuleSkeleton />
        </div>
        <div v-else>
            <b-img
                v-if="data.gptw"
                class="clickable"
                @click="toAboutGPTW"
                rounded
                :alt="data.gptw.text"
                :src="data.gptw.img"
                v-b-tooltip.hover.html
                :title="data.gptw.tooltip"
                height="60"
                style="float: right"
            />
            <div v-if="deadline">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.deadline') }}
                </h6>
                <div class="base-info">
                    <BaseIcon
                        v-if="deadline"
                        name="clock"
                        class="icon gray-color"
                    />
                    <h6 v-if="deadline" class="subtitle my-2">
                        {{ deadline }}
                    </h6>
                </div>
            </div>
            <div v-if="location">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.location') }}
                </h6>
                <div class="base-info">
                    <BaseIcon
                        v-if="location"
                        name="map-marker-alt"
                        class="icon gray-color"
                    />
                    <h6 v-if="location" class="subtitle my-2">
                        {{ location }}
                    </h6>
                </div>
            </div>
            <div v-if="employmentTypes().length > 0">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.employmentType') }}
                </h6>
                <ul>
                    <li
                        v-for="type in employmentTypes()"
                        :key="type"
                        class="subtitle"
                    >
                        {{ type }}
                    </li>
                </ul>
            </div>
            <div v-if="languages().length > 0">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.languageRequirements') }}
                </h6>
                <ul>
                    <li
                        v-for="lang in languages()"
                        :key="lang"
                        class="subtitle"
                    >
                        {{ lang }}
                    </li>
                </ul>
            </div>
            <div v-if="permits().length > 0">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.workPermitRequirements') }}
                </h6>
                <ul>
                    <li
                        v-for="permit in permits()"
                        :key="permit"
                        class="subtitle"
                    >
                        {{ permit }}
                    </li>
                </ul>
            </div>
            <div v-if="educationLevels().length > 0">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.preferredEducationLevel') }}
                </h6>
                <ul>
                    <li
                        v-for="level in educationLevels()"
                        :key="level"
                        class="subtitle"
                    >
                        {{ level }}
                    </li>
                </ul>
            </div>
            <div v-if="educations().length > 0">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.preferredEducationField') }}
                </h6>
                <ul>
                    <li
                        v-for="education in educations()"
                        :key="education"
                        class="subtitle"
                    >
                        {{ education }}
                    </li>
                </ul>
            </div>
            <div v-if="tags().length > 0">
                <h6 class="subtitle bold my-2">
                    {{ $t('jobdetail.detailModule.jobTags') }}
                </h6>
                <ul>
                    <li v-for="level in tags()" :key="level" class="subtitle">
                        {{ level }}
                    </li>
                </ul>
            </div>
            <div>
                <BaseButton
                    id="applyButton"
                    class="w-100"
                    :class="{ 'disabled-button': !isVerified }"
                    variant="secondary"
                    @click="apply"
                >
                    <div
                        class="d-flex align-items-center justify-content-center"
                    >
                        <BaseIcon v-if="!isVerified" name="lock" class="mr-1" />
                        <BaseIcon v-if="isPending" name="clock" class="mr-1" />
                        {{ $t('jobdetail.detailModule.apply') }}
                    </div>
                </BaseButton>
                <b-tooltip
                    v-if="!isVerified"
                    target="applyButton"
                    placement="bottom"
                >
                    {{ $t('jobdetail.detailModule.verificationRequiredHint') }}
                </b-tooltip>
                <b-tooltip
                    v-if="isPending"
                    target="applyButton"
                    placement="bottom"
                >
                    {{ $t('jobdetail.detailModule.verificationPendingHint') }}
                </b-tooltip>
            </div>
        </div>
    </Paper>
</template>

<script>
// Components
import DetailModuleSkeleton from './skeletons/DetailModuleSkeleton';

// Utils
import uuidv4 from '~/utils/uuidv4';
import AuthService from '~/services/AuthService';
import AnalyticService from '~/services/AnalyticService';
import DialogService from '~/services/DialogService';

import URLS from '~/URLS';

export default {
    name: 'DetailModule',
    components: { DetailModuleSkeleton },
    props: {
        data: {
            type: Object,
            default: {},
        },
        postingId: [String, Number],
        location: String,
        deadline: String,
        RequestId: String,
        impressionEventId: String,
        isVerified: Boolean,
        isPending: Boolean,
        isLoading: Boolean,
    },
    computed: {
        detailData() {
            return this.data;
        },
    },
    methods: {
        async apply() {
            // IF not logged in, open loginDialog
            if (!AuthService.isLoggedIn()) {
                this.$interfaces.logInModule.toggleOpen();
                return;
            } else if (this.isPending) {
                // Open pending message thing from avatars
                /*  let uniConnections = await this.$interfaces.university.connection.get();
                uniConnections = uniConnections.data.filter(
                    c => c.isVerified === false
                );
                if (uniConnections.length > 0) {
                    this.$interfaces.uniBadgeModule.setOpenAndSelected({
                        isOpen: true,
                        selected: uniConnections[0],
                    });
                } */
                // If logged in, but not verified, open verifyDialog
                DialogService.openUniVerifyDialog({
                    payload: {
                        checkConnections: true,
                    },
                });
                return;
            } else if (!this.isVerified) {
                // If logged in, but not verified, open verifyDialog
                DialogService.openUniVerifyDialog({
                    then: payload => {
                        if (payload instanceof Object && payload.isVerified) {
                            this.applyClick();
                        }
                    },
                    payload: {
                        checkConnections: true,
                    },
                });
                return;
            }

            this.applyClick();
        },

        applyClick() {
            // Log apply-button clicked
            AnalyticService.applyClickEvent(
                this.detailData.jobAdId,
                this.detailData.companyId,
                this.detailData.title
            );

            // Send event-request to Google CTS
            if (
                typeof this.RequestId === 'string' &&
                this.RequestId.length > 0
            ) {
                this.$interfaces.event.post({
                    body: {
                        requestId: this.RequestId,
                        eventId: 'JobApplyNowPressed' + uuidv4(),
                        parentEventId: this.impressionEventId,
                        createTime: new Date().toISOString(),
                        jobEvent: {
                            type: 'APPLICATION_REDIRECT',
                            jobs: [this.data.nameId],
                        },
                    },
                });
            }

            // Open for apply
            window.open(this.applyLink(), '_blank');
        },
        languages() {
            return this.detailData.preferedLanguages || [];
        },
        permits() {
            return this.detailData.workPermitRequirements || [];
        },
        educations() {
            return this.detailData.preferedEducationFields || [];
        },
        educationLevels() {
            return this.detailData.preferedEducationLevels || [];
        },
        employmentTypes() {
            return this.detailData.employmentTypes || [];
        },
        tags() {
            return this.detailData.tags || [];
        },
        applyLink() {
            const { link } = this.detailData;
            return link.startsWith('http') || link.startsWith('mailto:')
                ? link
                : 'https://' + link;
        },
        toAboutGPTW() {
            this.$router.push(URLS.aboutGPTW);
        },
    },
};
</script>

<style scoped>
.base-info {
    display: grid;
    grid-template-columns: 26px 1fr;
}
.icon {
    display: block;
    margin: auto;
}
.bold {
    font-weight: 700 !important;
}
</style>
